/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Grid, Stack, Dialog, DialogTitle, DialogContent, Tooltip } from "@mui/material";
import Box from "components/generals/Box";
import Typography from "components/generals/Typography";
import useHttp from "hooks/http.hook";
import { FC, useEffect, useMemo, useState, ChangeEvent, useRef } from "react";
import {
  AppointmentInterface,
  ProviderInterface,
  HeaderEnum,
  log,
  PatientInterface,
  PersonInterface,
  QueryResultInterface,
  VisitOfficeStatusEnum,
  VisitStatusEnum,
  VisitTypeEnum,
  FacilityInterface,
  ResourceInterface,
  CaseInterface,
  CaseAppointmentInterface,
  PatientInsuranceInterface,
  AccountingMovementInterface,
} from "medicalsystem-shared";
import FormField from "components/generals/FormInput";
import FormikForm from "components/layout/FormikForm";
import { Form } from "formik";
import { appointmentFormConfig, getSelectOptions } from "utils/form.utils";
import FormSelectInput from "components/generals/FormSelectInput";
import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { FormProps } from "../../interfaces";
import DatePicker from "components/generals/DatePicker";
import Button from "components/generals/Button";
import {
  AutoCompleteOption,
  EntityPermissions,
  FormActionType,
  PermissionsUser,
  SelectArrayType,
} from "types";
import { CoreAPI_URLs } from "constants/http.const";
import { Method } from "axios";
import { useTranslation } from "react-i18next";
import { eventAction, useAppDispatch } from "store/store";
import Autocomplete from "components/generals/Autocomplete";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutesConstants } from "medicalsystem-shared";
import PermissionWrapper from "components/generals/PermissionWrapper";
import BillingEncounterForm from "./AppointmentBillingEncountersForm";
import { useSelector } from 'react-redux';
import { selectIsDoctor, selectIsPatient } from "store/slices/auth.slice";
import Checkbox from "components/generals/Checkbox";
import CurrencyInput from "components/generals/CurrencyInput";
import VideocamIcon from "@mui/icons-material/Videocam";
import { useVideochat } from '../../hooks/videochat.hook';
import { useNavigationHistory } from "hooks/navigation.hook";
import { calculateAccountingTotals } from "utils/accountingMovements.utils";
import DialogContentText from "components/generals/DialogContentText";
import DialogActions from "components/generals/DialogActions";

/**
 * Props for AppointmentForm component.
 */
interface AppointmentFormProps extends FormProps<AppointmentInterface> {
  autoOpenVideochat?: boolean;
  comesFromBilling?: boolean;
}

/**
 * Comprehensive appointment scheduling and management form.
 * 
 * Features:
 * - Create, edit, and view appointments
 * - Patient and provider selection with autocomplete
 * - Facility and resource assignment
 * - Visit status management (scheduled, check-in, check-out, cancelled)
 * - Visit type selection (office, telehealth, emergency, etc.)
 * - Date/time picker with duration
 * - Case/episode of care association
 * - Insurance and copay management with override capability
 * - Non-billable appointment flag
 * - Appointment reason and notes
 * - Integration with billing encounters
 * - Videochat integration for telehealth appointments
 * - Automatic patient selection from navigation state
 * - Permission-based field visibility and editing
 * - Read-only mode for patients and doctors
 * - Calculate and display copay amounts
 * - Navigation to patient hub and office visit workflow
 * - Form validation with Formik
 * 
 * Role-Based Features:
 * - Patients: Read-only view, cannot modify appointments
 * - Doctors: Limited editing based on appointment status
 * - Admins: Full appointment management access
 * 
 * @param model - Appointment data to edit (or new appointment template)
 * @param action - Form action (NEW, MODIFY, READ_ONLY)
 * @param isModal - Display form in modal dialog
 * @param autoOpenVideochat - Automatically launch videochat on form open
 * @param comesFromBilling - Flag indicating navigation from billing page
 * @param onCloseAction - Callback when form is closed
 * @param updateListHandler - Callback to refresh parent appointment list
 * @returns Appointment form component
 */
const AppointmentForm: FC<AppointmentFormProps> = ({
  model,
  action,
  isModal,
  autoOpenVideochat,
  comesFromBilling,
  onCloseAction,
  updateListHandler,
}) => {
  const { t } = useTranslation();
  const [isLoading, _error, sendRequest] = useHttp();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { navigateWithHistory } = useNavigationHistory();
  const [doctorList, setDoctorList] = useState<ProviderInterface[]>([]);
  const [patientList, setPatientList] = useState<PatientInterface[]>([]);
  const [facilityList, setFacilityList] = useState<FacilityInterface[]>([]);
  const [resourceList, setResourceList] = useState<ResourceInterface[]>([]);
  const [patientInsuranceList, setPatientInsuranceList] = useState<PatientInsuranceInterface[]>([]);
  const [patientSelectedId, setPatientSelectedId] = useState<string>("");
  const [caseSelected, setCaseSelected] = useState<CaseInterface>();
  const [caseList, setCaseList] = useState<CaseInterface[]>([]);
  const [isBillingModalOpen, setIsBillingModalOpen] = useState<boolean>(false);
  const location = useLocation();
  const [nonBillable, setNonBillable] = useState<boolean>(false);
  const [overrideCopay, setOverrideCopay] = useState<boolean>(false);
  const state = (location.state as { data?: string; selected?: boolean }) || {};
  const patient = state?.data;
  model.patient =  (patient??model.patient) as Partial<PatientInterface>;
  function handleBack() {
    navigate(-1);
  }
  const videochat = useVideochat();
  const isPatient = useSelector(selectIsPatient);
  const isDoctor = useSelector(selectIsDoctor);
  const shouldDisableButtons = useMemo(() => 
    model.visitStatus === VisitStatusEnum.CHECK_IN && new Date(model.arrivalDateTime) < new Date() || isPatient,
    [model.visitStatus, model.arrivalDateTime, isPatient]
  );

  const isReadOnlyMode = useMemo(() => {
    if(isPatient || isDoctor|| action==FormActionType.READ_ONLY) return true;
  }, [action]);

  useEffect(() => {
    sendRequest(CoreAPI_URLs.DOCTOR_GET_ALL, onGetDoctorsHandler, "GET");
    sendRequest(CoreAPI_URLs.PATIENTS_GET_ALL, onGetPatientsHandler, "GET");
    sendRequest(CoreAPI_URLs.FACILITY_GET_ALL, onGetFacilityHandler, "GET");
    sendRequest(CoreAPI_URLs.RESOURCES_GET_ALL, onGetResourceHandler, "GET");
    if(patient) {
      setPatientSelectedId(model.patient.id);
    }
    if(action === FormActionType.MODIFY)
    {
      setNonBillable(model.nonBillable);
      setOverrideCopay(Boolean(model.overrideCopay));
    }
    if(model.patient)
    {
      if(action !== FormActionType.NEW)
      {
        getAccountingId(model.patient.id);
      }
      sendRequest(CoreAPI_URLs.PATIENT_INSURANCE_GET_ALL,onGetPatientInsuranceHandler, "GET",null,[{ header: HeaderEnum.PATIENT_ID, value: model.patient.id }]);
    }
  }, []);

  useEffect(() => {
    if (patientSelectedId && action == FormActionType.NEW && !isPatient) {
      sendRequest(CoreAPI_URLs.MEDICAL_CASE_GET_ALL,onGetCaseHandler, "GET",null,[{ header: HeaderEnum.PATIENT_ID, value: patientSelectedId }]);
      sendRequest(CoreAPI_URLs.PATIENT_INSURANCE_GET_ALL, onGetPatientInsuranceHandler, "GET",null,[{ header: HeaderEnum.PATIENT_ID, value: patientSelectedId }]);
    }
  }, [patientSelectedId, isPatient]);

  const onGetCaseHandler = (rquestList: QueryResultInterface<CaseInterface>) => {
    if (rquestList.results.length > 0) {
      setCaseList(rquestList.results);
    } else {
      setCaseList([]);
    }
  };

  const isNearAppointmentTime = useMemo(() => {
    if (!model.arrivalDateTime) {
      return false;
    }
    const now = new Date();
    const appointmentTime = new Date(model.arrivalDateTime);
    if (isNaN(appointmentTime.getTime())) {
      return false;
    }
    const condition1 = appointmentTime.getTime() >= now.getTime() - 15 * 60 * 1000;
    const condition2 = appointmentTime.getTime() <= now.getTime() + 5 * 60 * 1000;
    return condition1 && condition2;
  }, [model.arrivalDateTime]);

  const caseOptions = useMemo<AutoCompleteOption[]>(() => {
    if (caseList.length === 0) {
      return [];
    } else {
      return caseList.map((a) => ({
        label: `${a.label} - ${a.typeCase}`.toUpperCase(),
        id: a.id,
      }));
    }
  }, [caseList]);

  function handleNewProgressNote() {
    const data = model.id;
    navigateWithHistory(RoutesConstants.PROGRESS_NOTE, { 
      state: { 
        data
      } 
    });
  }
  function handleOpenVideochatModal() {
    if (model.id && model.patient?.person && model.provider?.person && isNearAppointmentTime) {
      videochat.startFloatingCall(
        model.id,
        `${model.patient.person.name} ${model.patient.person.lastname}`,
        `${model.provider.person.name} ${model.provider.person.lastname}`
      );
    }
  }
  function handleNewVitals() {
    const data = model.id;
    navigateWithHistory(RoutesConstants.VITALS, { 
      state: { savedEntityId: data }, 
      savedEntityId: data 
    });
  }
  
  function handleOpenPatientHub() {
    const data = model.patient.id;
    navigateWithHistory(RoutesConstants.PATIENT_HUB, { 
      state: { 
        data
      } 
    });
  }

  function handleOpenBillingModal() {
    setIsBillingModalOpen(true);
  }

  function handleCloseBillingModal() {
    setIsBillingModalOpen(false);
  }

  const onGetDoctorsHandler = (data: QueryResultInterface<ProviderInterface>) => {
    const sortedDoctors = data.results.sort((a, b) => a.person.name.localeCompare(b.person.name));
    setDoctorList(sortedDoctors);
  };

  const onGetPatientsHandler = (data: QueryResultInterface<PatientInterface>) => {
    const sortedPatient = [...data.results];
    sortedPatient.sort((a, b) => a.person.name.localeCompare(b.person.name));
    setPatientList(sortedPatient);
  };
  
  const onGetFacilityHandler = (data: QueryResultInterface<FacilityInterface>) => {
    const sortedFacility = [...data.results];
    sortedFacility.sort((a, b) => a.name.localeCompare(b.name));
    setFacilityList(sortedFacility);
  };
  
  const onGetResourceHandler = (data: QueryResultInterface<ResourceInterface>) => {
    const sortedResources = [...data.results];
    sortedResources.sort((a, b) => a.name.localeCompare(b.name));
    setResourceList(sortedResources);
  };
  
  const onGetPatientInsuranceHandler = (data: QueryResultInterface<PatientInsuranceInterface>) => {
    const sortedInsurances = [...data.results];
    sortedInsurances.sort((a, b) => a.insurance.name.localeCompare(b.insurance.name));
    setPatientInsuranceList(sortedInsurances);
  };

  const SELECT_OPTION = useMemo<AutoCompleteOption>(() => {
    return { label: "[SELECT]", ID: "" };
  }, []);

  const getFullName = (person: Partial<PersonInterface>, id: string) => {
    let fullName: AutoCompleteOption;
    if (id && person) {
      fullName = {
        label: person.name + " " + person.lastname,
        id: id,
      };
    } else {
      fullName = SELECT_OPTION;
    }
    return fullName;
  };

  const doctorOptions = useMemo<AutoCompleteOption[]>(() => {
    const result: AutoCompleteOption[] = [];
    doctorList.forEach((d) => {
      if (d.person) {
        result.push(getFullName(d.person, d.id));
      }
    });
    return result;
  }, [doctorList]);

  const patientOptions = useMemo<AutoCompleteOption[]>(() => {
    const result: AutoCompleteOption[] = [];
    patientList.forEach((p) => {
      if (p.person) {
        result.push(getFullName(p.person, p.id));
      }
    });
    return result;
  }, [patientList]);

  const facilityOptions = useMemo<AutoCompleteOption[]>(() => {
    if (facilityList.length === 0) {
      return [];
    } else {
      return facilityList.map((a) => ({
        label: `${a.name} - ${a.city}`.toUpperCase(),
        id: a.id,
      }));
    }
  }, [facilityList]);

  const resourceOptions = useMemo<AutoCompleteOption[]>(() => {
    if (facilityList.length === 0) {
      return [];
    } else {
      return resourceList.map((a) => ({
        label: `${a.name}`.toUpperCase(),
        id: a.id,
      }));
    }
  }, [resourceList]);

  const insuranceOptions = useMemo<AutoCompleteOption[]>(() => {
    if (patientInsuranceList.length === 0) {
      return [];
    } else {
      return patientInsuranceList.map((a) => ({
        label: `${a.policyId} - ${a.insurance.name}`.toUpperCase(),
        id: a.id,
      }));
    }
  }, [patientInsuranceList]);

  const visitTypeListOptions = useMemo<SelectArrayType[]>(
    () => getSelectOptions(VisitTypeEnum, t),
    []
  );
  
  const visitStatusListOptions = useMemo<SelectArrayType[]>(
    () => getSelectOptions(VisitStatusEnum, t),
    []
  );

  const visitOfficeStatus = useMemo<SelectArrayType[]>(
    () => getSelectOptions(VisitOfficeStatusEnum, t),
    []
  );

  const getProviderDisplayValue = () => {
    if (model?.provider?.person) {
      return `${model.provider.person.name} ${model.provider.person.lastname}`;
    }
    return "";
  };

  const getPatientDisplayValue = () => {
    if (model?.patient?.person) {
      return `${model.patient.person.name} ${model.patient.person.lastname}`;
    }
    return "";
  };

  const getFacilityDisplayValue = () => {
    if (model?.facility) {
      return `${model.facility.name} - ${model.facility.city}`.toUpperCase();
    }
    return "";
  };

  const getResourceDisplayValue = () => {
    if (model?.resource) {
      return model.resource.name.toUpperCase();
    }
    return "";
  };

  const getPatientInsuranceDisplayValue = () => {
    if (model?.patientInsurance) {
      return model.patientInsurance.insurance.name.toUpperCase();
    }
    return "";
  };

  const getVisitTypeDisplayValue = () => {
    const visitType = visitTypeListOptions.find(option => option.value === model.visitType);
    return visitType ? visitType.label : "";
  };

  const getVisitStatusDisplayValue = () => {
    const visitStatus = visitStatusListOptions.find(option => option.value === model.visitStatus);
    return visitStatus ? visitStatus.label : "";
  };

  const getVisitOfficeStatusDisplayValue = () => {
    const officeStatus = visitOfficeStatus.find(option => option.value === model.visitOfficeStatus);
    return officeStatus ? officeStatus.label : "";
  };

  const nonBillableCheckHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const checked = event.currentTarget.checked;
    setNonBillable(checked);
  };

  const overrideCopayCheckHandler = (event: ChangeEvent<HTMLInputElement>, setFieldValue: (field: string, value: any) => void) => {
    const checked = event.currentTarget.checked;
    setOverrideCopay(checked);
    if (!checked) {
      setFieldValue("overrideCopay", 0);
    }
  };

  const submitHandler = async (appointment: Partial<AppointmentInterface>) => {
    if (isPatient) return;
    if (appointment?.vitals?.id == null) {
      appointment.vitals = undefined;
    }
    let method: Method = "GET";
    if (action === FormActionType.NEW) {
      method = "POST";
    } else if (action === FormActionType.MODIFY) {
      method = "PUT";
    }
    appointment.overrideCopay = overrideCopay === false ? null : appointment.overrideCopay;
    appointment.nonBillable= nonBillable;
    const data = JSON.stringify(appointment);
    await sendRequest(CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS, onSaveHandler, method, data, [
      { header: HeaderEnum.RESOURCE_ID, value: model.id },
    ]);
  };

  const onSaveHandler = async (data: AppointmentInterface) => {
    dispatch(
      eventAction.addSuccessMessage({
        message:
          action === FormActionType.NEW
            ? t("appointmentForm.successCreated")
            : t("appointmentForm.successEdited"),
        id: crypto.randomUUID(),
      })
    );

    if (caseSelected) {
      const newCaseAppointment: CaseAppointmentInterface = {
        appointment: data,
        medicalCase: caseSelected,
      };
      await sendRequest(
        CoreAPI_URLs.CASE_APPOINTMENT_GENERAL_OPERATIONS,
        null,
        "POST",
        JSON.stringify(newCaseAppointment)
      );
    }
    if (action === FormActionType.NEW) {
      navigateWithHistory(RoutesConstants.APPOINTMENTS);
    }
    if (isModal) {
      sendRequest(CoreAPI_URLs.APPOINTMENTS_GENERAL_OPERATIONS, null, "GET", null, [
        { header: HeaderEnum.RESOURCE_ID, value: data.id },
      ]).then((resp) => {
        if (resp.status === 200) {
          updateListHandler && updateListHandler(resp.data, true);
        }
      });
    } else {
      updateListHandler && updateListHandler(data);
    }
    onCloseAction && onCloseAction();
  };
 
  const getAccountingId =(patientId:string)=>{
    sendRequest(CoreAPI_URLs.ACCOUNTING_MOVEMENT_GET_ALL, onGetAccountingIdHandler, "GET", null, [{ header: HeaderEnum.PATIENT_ID, value: patientId }]);
  }

  const onGetAccountingIdHandler = (data: QueryResultInterface<AccountingMovementInterface>) => {
    const { balancePatient } = calculateAccountingTotals(data.results);
    if (balancePatient > 0) {
      handleOpenDuePayModal();
    }
  };
  const [openDuePayModal, setOpenDuePayModal] = useState(false);
      const handleOpenDuePayModal = () => {
    setOpenDuePayModal(true);
  };

  const handleCloseDuePayModal = () => {
    setOpenDuePayModal(false);
  };

  const hasOpenedVideochatRef = useRef(false);

  useEffect(() => {
    const shouldAutoOpen = Boolean(autoOpenVideochat) && window.location.hash === "#videochat";
    if (
      shouldAutoOpen &&
      !hasOpenedVideochatRef.current &&
      model?.id &&
      model.patient?.person &&
      model.provider?.person
    ) {
      hasOpenedVideochatRef.current = true;
      handleOpenVideochatModal();
      try {
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      } catch {}
    }
  }, [autoOpenVideochat, model?.id]);

  return (
    <>
      <FormikForm
        onSubmit={(model) => {
          if (!isPatient) {
            submitHandler(model);
          }
        }}
        initialValues={model}
        validationSchema={appointmentFormConfig(t,nonBillable)}
      >
        {({    values, 
          errors, 
          touched, 
          setFieldValue, 
          validateForm, 
          setTouched, 
          submitForm  }) => {
          return (
          <Form id="appointmentData" autoComplete="off">
            <Box className="form-container" p={1.5}>
              <Stack alignItems="center" direction={{ xs: "column", sm: "row" }} gap={2}>
                <EditCalendarIcon fontSize="large" />
                <Typography variant="h3" textAlign={{ xs: "center", sm: "left" }}>
                  {t("appointmentForm.title")}
                  {isPatient && (
                    <Typography variant="body2" color="secondary" sx={{ ml: 1 }}>
                      ({t("appointmentForm.readOnlyMode")})
                    </Typography>
                  )}
                </Typography>
              </Stack>
              <br />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Stack
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    direction={{ xs: "column", sm: "row" }}
                    gap={2}
                  >
                    {isReadOnlyMode || shouldDisableButtons ? (
                      <FormField
                        type="text"
                        name="arrivalDateTime"
                        label={t("appointmentForm.arrivalDateTime")}
                        fullWidth
                        value={values.arrivalDateTime ? new Date(values.arrivalDateTime).toLocaleString() : ""}
                        disabled={true}
                        id="cy-arrivalDateTime"
                      />
                    ) : (
                    <Grid id="cy-arrivalDateTime" width={"100%"}>
                      <DatePicker
                        key={model.id || 'new'}
                        name="arrivalDateTime"
                        value={values.arrivalDateTime}
                        label={t("appointmentForm.arrivalDateTime")}
                        error={errors.arrivalDateTime && touched.arrivalDateTime}
                        disabled={isReadOnlyMode}
                        options={{
                          enableTime: true,
                          dateFormat: "Y-m-d H:i",
                          minDate: action === FormActionType.NEW 
                            ? new Date() 
                            : (values.arrivalDateTime && new Date(values.arrivalDateTime) < new Date() 
                                ? new Date(values.arrivalDateTime) 
                                : new Date()),
                          defaultDate: values.arrivalDateTime || new Date(new Date().getTime() + 60 * 60 * 1000),
                        }}
                        onChange={(selectedDate) => {
                          if (!isReadOnlyMode && selectedDate && selectedDate[0]) {
                            setFieldValue("arrivalDateTime", selectedDate[0]);
                          }
                        }}
                      />
                    </Grid>
                    )}
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField
                    type="number"
                    name="duration"
                    label={t("appointmentForm.duration")}
                    fullWidth
                    value={values.duration}
                    error={errors.duration && touched.duration}
                    disabled={isReadOnlyMode}
                    id="cy-duration"
                  />
                </Grid>
                <Grid item xs={12} md={6} id="cy-provideridFormContainer">
                  {isReadOnlyMode ? (
                    <FormField
                      type="text"
                      name="provider"
                      label={t("appointmentForm.doctorProvider")}
                      fullWidth
                      value={getProviderDisplayValue()}
                      disabled={true}
                      id="cy-provider-readonly"
                    />
                  ) : (
                    <Autocomplete
                        options={doctorOptions}
                        loading={isLoading}
                        name="provider.id"
                        label={t("appointmentForm.doctorProvider")}
                        onChange={(_e, value) => {
                          if (value && !isReadOnlyMode) {
                            setFieldValue("provider.id", value.id);
                          }
                        }}
                        defaultValue={
                        model?.provider?.id
                        ? {
                          id: model.provider.id,
                          label: model.provider.person.name + " " + model.provider.person.lastname,
                        }
                        : null
                      }
                      id="cy-providerid"
                      noOptionsText={t("appointmentForm.noOptions")}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6} id="cy-patientidFormContainer">
                  {isReadOnlyMode ? (
                    <FormField
                      type="text"
                      name="patient"
                      label={t("appointmentForm.patient")}
                      fullWidth
                      value={getPatientDisplayValue()}
                      disabled={true}
                      id="cy-patient-readonly"
                    />
                  ) : (
                    <Autocomplete
                      options={patientOptions}
                      loading={isLoading}
                      name="patient.id"
                      label={t("appointmentForm.patient")}
                      onChange={(_e, value) => {
                        if (value && !isReadOnlyMode) {
                          setPatientSelectedId(value.id);
                          setFieldValue("patient.id", value.id);
                          setFieldValue("patientInsurance", null);
                          getAccountingId(value.id);
                        }
                      }}
                      defaultValue={
                        model?.patient?.id
                          ? {
                              id: model.patient.id,
                              label: model.patient.person.name + " " + model.patient.person.lastname,
                            }
                          : null
                      }
                      id="cy-patientid"
                      noOptionsText={t("appointmentForm.noOptions")}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6} id="cy-facilityidFormContainer">
                  {isReadOnlyMode ? (
                    <FormField
                      type="text"
                      name="facility"
                      label={t("appointmentForm.facility")}
                      fullWidth
                      value={getFacilityDisplayValue()}
                      disabled={true}
                      id="cy-facility-readonly"
                    />
                  ) : (
                    <Autocomplete
                      options={facilityOptions}
                      loading={isLoading}
                      name="facility.id"
                      label={t("appointmentForm.facility")}
                      onChange={(_e, value) => {
                        if (value && !isReadOnlyMode) {
                          setFieldValue("facility.id", value.id);
                        }
                      }}
                      defaultValue={
                        model?.facility?.id
                          ? {
                              id: model.facility.id,
                              label: model.facility.name + " " + model.facility.city,
                            }
                          : null
                      }
                      id="cy-facilityid"
                      noOptionsText={t("appointmentForm.noOptions")}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6} id="cy-resourceidFormContainer">
                  {isReadOnlyMode ? (
                    <FormField
                      type="text"
                      name="resource"
                      label={t("appointmentForm.resource")}
                      fullWidth
                      value={getResourceDisplayValue()}
                      disabled={true}
                      id="cy-resource-readonly"
                    />
                  ) : (
                    <Autocomplete
                      options={resourceOptions}
                      loading={isLoading}
                      name="resource.id"
                      label={t("appointmentForm.resource")}
                      onChange={(_e, value) => {
                        if (value && !isReadOnlyMode) {
                          setFieldValue("resource.id", value.id);
                        }
                      }}
                      defaultValue={
                        model?.resource?.id
                          ? {
                              id: model.resource.id,
                              label: model.resource.name,
                            }
                          : null
                      }
                      id="cy-resourceid"
                      noOptionsText={t("appointmentForm.noOptions")}
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {isReadOnlyMode ? (
                    <FormField
                      type="text"
                      name="visitType"
                      label={t("appointmentForm.visitType")}
                      fullWidth
                      value={getVisitTypeDisplayValue()}
                      disabled={true}
                      id="cy-visitType-readonly"
                    />
                  ) : (
                    <FormSelectInput
                      name="visitType"
                      label={t("appointmentForm.visitType")}
                      selectArray={visitTypeListOptions}
                      defaultValue={values.visitType}
                      id="cy-visitType"
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {isReadOnlyMode ? (
                    <FormField
                      type="text"
                      name="visitStatus"
                      label={t("appointmentForm.visitStatus")}
                      fullWidth
                      value={getVisitStatusDisplayValue()}
                      disabled={true}
                      id="cy-visitStatus-readonly"
                    />
                  ) : (
                    <Box
                    >
                      <FormSelectInput
                        name="visitStatus"
                        label={t("appointmentForm.visitStatus")}
                        selectArray={visitStatusListOptions}
                        defaultValue={values.visitStatus}
                        id="cy-visitStatus"
                      />
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {isReadOnlyMode ? (
                    <FormField
                      type="text"
                      name="visitOfficeStatus"
                      label={t("appointmentForm.visitOfficeStatus")}
                      fullWidth
                      value={getVisitOfficeStatusDisplayValue()}
                      disabled={true}
                      id="cy-visitOfficeStatus-readonly"
                    />
                  ) : (
                    <FormSelectInput
                      name="visitOfficeStatus"
                      label={t("appointmentForm.visitOfficeStatus")}
                      selectArray={visitOfficeStatus}
                      defaultValue={values.visitOfficeStatus}
                      id="cy-visitOfficeStatus"
                    />
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField
                    type="text"
                    name="email"
                    label={t("appointmentForm.email")}
                    fullWidth
                    value={values.email}
                    disabled={isReadOnlyMode}
                    id="cy-email"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField
                    type="text"
                    name="diagnosis"
                    label={t("appointmentForm.diagnosis")}
                    fullWidth
                    value={values.diagnosis}
                    disabled={isReadOnlyMode}
                    id="cy-diagnosis"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormField
                    type="text"
                    name="roomNumber"
                    label={t("appointmentForm.roomNumber")}
                    fullWidth
                    value={values.roomNumber}
                    error={errors.roomNumber && touched.roomNumber}
                    disabled={isReadOnlyMode}
                    id="cy-roomNumber"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormField
                    label={t("appointmentForm.visitReason")}
                    name="reason"
                    type="text"
                    multiline
                    fullWidth
                    error={errors.reason && touched.reason}
                    value={values.reason}
                    disabled={isReadOnlyMode}
                    id="cy-reason"
                  />
                </Grid>
                {action === FormActionType.NEW && !isPatient && (
                  <Grid item xs={12} id="cy-patientidFormContainer">
                    <Autocomplete
                      options={caseOptions}
                      loading={isLoading}
                      name="medicalCase"
                      label={t("appointmentForm.medicalCase")}
                      id="cy-medicalCase"
                      onChange={(_e, value) => {
                        if (value) {
                          const selectedCase = caseList.find((caseItem) => caseItem.id === value.id);
                          if (selectedCase) {
                            setCaseSelected(selectedCase);
                          }
                        }
                      }}
                      noOptionsText={t("appointmentForm.noOptions")}
                    />
                  </Grid>
                )}
                <Grid item xs={12} md={12} lg={6}>
                  <Grid item xs={12}>
                    <Typography variant="h5">{t("appointmentForm.changesForVisit")}</Typography>
                  </Grid>
                  <Grid item xs={12} md={12} display="flex" sx={{alignItems: "center"}}>
                    <Checkbox
                      id="cy-nonBillable"
                      label={t("appointmentForm.nonBillable")}
                      name="nonBillable"
                      value={nonBillable}
                      checked={nonBillable}
                      onChange={nonBillableCheckHandler}
                      disabled={isReadOnlyMode}
                    />
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} display={"flex"} mb={3} sx={{alignItems: "center"}}>
                    <Checkbox
                      label={t("appointmentForm.changeCopay")}
                      name="NoName"
                      value={overrideCopay}
                      checked={overrideCopay}
                      onChange={(e) => overrideCopayCheckHandler(e, setFieldValue)}
                      disabled={isReadOnlyMode}
                    />
                      <CurrencyInput
                        sx={{width: 130}}
                        name="overrideCopay"
                        disabled={!overrideCopay}
                        value={values.overrideCopay || 0}
                        onChange={(value) => setFieldValue("overrideCopay", value)}
                      />
                  </Grid>
                </Grid>
                <Grid item xs={12} md={12} lg={6} id="cy-insuranceidFormContainer">
                  <Typography variant="h5">{t("appointmentForm.policy")}</Typography>
                  {isReadOnlyMode ? (
                    <FormField
                      type="text"
                      name="patientInsurance"
                      label={t("appointmentForm.insurance")}
                      fullWidth
                      value={getPatientInsuranceDisplayValue()}
                      disabled={true}
                      id="cy-insurance-readonly"
                    />
                  ) : (
                    <Autocomplete
                      key={patientSelectedId}
                      options={insuranceOptions}
                      loading={isLoading}
                      name="patientInsurance.id"
                      label={t("appointmentForm.insurance")}
                      error={Boolean(errors?.patientInsurance?.id && touched?.patientInsurance?.id) || Boolean(errors?.patientInsurance && touched?.patientInsurance)}
                      onChange={(_e, value) => {
                        if (value && !isReadOnlyMode) {
                          setFieldValue("patientInsurance.id", value.id);
                        }
                      }}
                      defaultValue={
                        model?.patientInsurance?.id
                          ? {
                            id: model.patientInsurance.id,
                            label: model.patientInsurance.policyId + " - " + model.patientInsurance?.insurance?.name ,
                          }
                          : null
                      }
                      id="cy-patientinsuranceid"
                      noOptionsText={t("appointmentForm.noOptions")}
                    />
                  )}
                </Grid>
                <Grid container spacing={2} className="appointment-form__button-group"> 
                {!isReadOnlyMode || !isPatient && (
                      <PermissionWrapper
                        requiredPermissions={[PermissionsUser[EntityPermissions.VITALS_PAGE].VIEW]}
                      >
                        <Button
                          className="appointment-form__button"
                          style={{ display: !model.id ? "none" : null }}
                          color="info"
                          onClick={handleNewVitals}
                        >
                          {t("appointmentForm.addVitals")}
                  </Button>
                </PermissionWrapper>
                    )}
          {!isPatient && (
            <>
                <PermissionWrapper
                  requiredPermissions={[PermissionsUser[EntityPermissions.PROGRESS_NOTE].VIEW]}
                >
                  <Tooltip 
                    title={shouldDisableButtons ? t("appointmentForm.cannotAccessProgressNoteWhenARR") : ""} 
                    arrow
                  >
                    <span>
                      <Button
                        id="cy-open-progress-note-button"
                        className="appointment-form__button"
                        style={{ display: !model.id ? "none" : null }}
                        color="info"
                        onClick={handleNewProgressNote}
                        disabled={shouldDisableButtons}
                      >
                        {t("appointmentForm.progressNoteOpen")}
                    </Button>
                    </span>
                  </Tooltip>
                </PermissionWrapper>
                <PermissionWrapper
                  requiredPermissions={[PermissionsUser[EntityPermissions.PATIENT_HUB].VIEW]}
                >
                  <Tooltip 
                    title={shouldDisableButtons ? t("appointmentForm.cannotAccessPatientHubWhenARR") : ""} 
                    arrow
                  >
                    <span>
                      <Button
                        id="cy-open-patient-hub"
                        className="appointment-form__button"
                        style={{ display: !model.id ? "none" : null }}
                        color="info"
                        onClick={handleOpenPatientHub}
                        disabled={shouldDisableButtons}
                      >
                        {t("appointmentForm.openPatientHUB")}
                      </Button>
                    </span>
                  </Tooltip>
                </PermissionWrapper>
                {!isReadOnlyMode && (
                  <PermissionWrapper
                    requiredPermissions={[PermissionsUser[EntityPermissions.BILLING_ENCOUNTER].CREATE]}
                  >
                    <Button
                      id="cy-add-billing"
                      className="appointment-form__button"
                      style={{ display: !model.id ? "none" : null }}
                      color="success"
                      onClick={handleOpenBillingModal}
                      startIcon={<ReceiptIcon />}
                    >
                      {t("appointmentForm.createBilling")}
                    </Button>
                  </PermissionWrapper>
                      )}
                  </>
                )}
                {(!comesFromBilling && isNearAppointmentTime) && (
                <PermissionWrapper
                  requiredPermissions={[PermissionsUser[EntityPermissions.VIDEOCHAT].VIEW]}
                >
                  <Button
                    id="cy-start-videochat"
                    className="appointment-form__button"
                    style={{ display: !model.id ? "none" : null }}
                    color="primary"
                    onClick={handleOpenVideochatModal}
                    startIcon={<VideocamIcon />}
                  >
                    {t("appointmentForm.startVideochat")}
                  </Button>
                </PermissionWrapper>)}
                </Grid>
              </Grid>
            </Box>
            <Box>
              <Grid container justifyContent="space-between">
                <Grid md={6} item>
                  {isModal ? (
                    <Button
                      onClick={() => onCloseAction && onCloseAction()}
                      type="submit"
                      color="secondary"
                      disabled={isLoading}
                    >
                      {t("appointmentForm.close")}
                    </Button>
                  ):(                  <Button
                       onClick={handleBack}
                      type="submit"
                      color="secondary"
                      disabled={isLoading}
                    >
                      {t("appointmentForm.cancel")}
                    </Button>)}
                </Grid>
                <Grid md={6} item textAlign="right">
                  {!isReadOnlyMode && (
                    <PermissionWrapper
                      requiredPermissions={[
                        PermissionsUser[EntityPermissions.NEW_APPOINTMENT].CREATE,
                        PermissionsUser[EntityPermissions.NEW_APPOINTMENT].EDIT,
                      ]}
                    >
                      <Button
                        onClick={async () => {
                          const formErrors = await validateForm(values);
                          if (Object.keys(formErrors).length > 0) {
                            setTouched({
                              provider: { id: true },
                              patient: { id: true },
                              facility: { id: true },
                              resource: { id: true },
                              patientInsurance: { id: true },
                              arrivalDateTime: true,
                              roomNumber: true,
                              reason: true
                            }, true);
                          } else {
                            submitForm();
                          }
                        }}
                        color="primary"
                        disabled={isLoading||isReadOnlyMode}
                        id="cy-createSaveAppointment"
                      >
                        {action === FormActionType.NEW
                          ? t("appointmentForm.create")
                          : t("appointmentForm.save")}
                      </Button>
                    </PermissionWrapper>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Form>
        );
        }}
      </FormikForm>
      {!isPatient && (
        <>
        <Dialog
          open={isBillingModalOpen}
          onClose={handleCloseBillingModal}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              minWidth: "80vw",
              minHeight: "70vh",
            },
          }}
        >
          <DialogTitle>
            <Stack direction="row" alignItems="center" gap={1}>
              <ReceiptIcon />
              <Typography variant="h5">
                {t("billingEncounterForm.title")}
              </Typography>
            </Stack>
          </DialogTitle>
          <DialogContent>
            {model.id && model.patient?.id && (
              <BillingEncounterForm
                appointmentId={model.id}
                patientId={model.patient.id}
                onCloseAction={handleCloseBillingModal}
                insurance={model.patientInsurance?true:false}
                nonBillable={model.nonBillable}
              />
            )}
          </DialogContent>
        </Dialog>
        <Dialog
        open={openDuePayModal}
        onClose={handleCloseDuePayModal}
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description"
        disableEnforceFocus
        keepMounted={false}
        >
        <DialogTitle id="error-dialog-title">{t('billingEncounterForm.titleDuePay')}</DialogTitle>
        <DialogContent>
          <DialogContentText id="error-dialog-description">{t('billingEncounterForm.descriptionDuePay')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button id="cy-close-due-pay-modal" onClick={handleCloseDuePayModal} color="primary">
            {t('billingEncounterForm.ok')}
          </Button>
        </DialogActions>
      </Dialog> 
         </>
      )}
    </>
);
}

export default AppointmentForm;